import { StandardEvents } from '@shopify/events';

/**
 * Vertical Oceans — Selling plan picker (progressive enhancement).
 *
 * The selector itself is native HTML: radios named `selling_plan` inside the
 * product form, so Shopify's cart/add receives the plan with no JS. This
 * element only adds two enhancements:
 *   1. On variant change, morph the picker's prices/availability from the
 *      re-rendered section HTML (so the per-option prices track the variant),
 *      preserving the shopper's selected plan when still available.
 *   2. Swap the add-to-cart button label to "Subscribe" when a plan is chosen.
 *
 * If this script fails to load, the radios still submit correctly — the page
 * just keeps the default "Add to cart" label and one-time price display.
 */
class SellingPlanPicker extends HTMLElement {
  #connected = false;
  /** @type {HTMLElement | null} */
  #section = null;
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLElement | null} */
  #cta = null;
  /** @type {string | null} */
  #defaultCtaText = null;

  connectedCallback() {
    if (this.#connected) return;
    this.#connected = true;

    this.#section = this.closest('.shopify-section, dialog, product-card');
    this.#form = this.closest('form');
    this.#cta =
      /** @type {HTMLElement | null} */ (this.#form?.querySelector('[data-testid="standalone-add-to-cart"]')) ||
      /** @type {HTMLElement | null} */ (this.#form?.querySelector('.add-to-cart-button'));

    this.addEventListener('change', this.#onChange);
    if (this.#section) {
      this.#section.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal: undefined });
    }

    this.#syncCta();
    this.#syncPrice();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    // productSelect listeners are left to the section's lifetime; this element
    // is rarely disconnected independently.
  }

  /** @returns {HTMLInputElement | null} */
  get #selectedRadio() {
    return this.querySelector('input[data-vo-sp-radio]:checked');
  }

  /** @returns {string} selected selling plan id, or '' for one-time */
  get #planId() {
    return this.#selectedRadio?.value ?? '';
  }

  #onChange = () => {
    this.#syncCta();
    this.#syncPrice();
  };

  /** Updates the add-to-cart button label to reflect the chosen option. */
  #syncCta() {
    if (!this.#cta) return;
    const labelEl =
      this.#cta.querySelector('.add-to-cart-text__content > span > span') ||
      this.#cta.querySelector('.add-to-cart-text__content > span') ||
      this.#cta.querySelector('.add-to-cart-text__content');
    if (!labelEl) return;

    if (this.#defaultCtaText == null) {
      this.#defaultCtaText = labelEl.textContent?.trim() || '';
    }
    const subscribeLabel = this.dataset.subscribeLabel || 'Subscribe';
    const onetimeLabel = this.dataset.onetimeLabel || '';
    labelEl.textContent = this.#planId
      ? subscribeLabel
      : (onetimeLabel || this.#defaultCtaText);
  }

  /** @returns {Element[]} every <product-price> for this product (main + sticky/mobile). */
  #priceTargets() {
    if (this.dataset.productId) {
      return Array.from(
        document.querySelectorAll(`product-price[data-product-id="${CSS.escape(this.dataset.productId)}"]`)
      );
    }
    return this.#section ? Array.from(this.#section.querySelectorAll('product-price')) : [];
  }

  /**
   * Reflect the selected plan's price in every theme price block for this product. The
   * price + compare-at are carried on the radios as data-vo-price / data-vo-compare
   * (formatted in vo-selling-plan-picker.liquid to match snippets/price.liquid), so this
   * needs no snapshot and no extra fetch — switching back to one-time restores the
   * one-time display (including an on-sale variant's own compare-at). Volume-pricing
   * price blocks (no .price__regular/.price__sale) are left untouched.
   */
  #syncPrice() {
    const radio = this.#selectedRadio;
    if (!radio) return;
    const priceText = radio.dataset.voPrice || '';
    const compareText = radio.dataset.voCompare || '';
    for (const pc of this.#priceTargets()) {
      const container = pc.querySelector('[ref="priceContainer"]');
      if (!container) continue;
      const regular = container.querySelector('.price__regular');
      const sale = container.querySelector('.price__sale');
      if (!regular || !sale) continue; // volume-pricing layout — leave as-is
      if (compareText) {
        regular.classList.add('price__hidden');
        sale.classList.remove('price__hidden');
        const salePrice = sale.querySelector('.price-item--sale.price') || sale.querySelector('.price');
        const compareAt = sale.querySelector('.compare-at-price');
        if (salePrice) salePrice.textContent = priceText;
        if (compareAt) compareAt.textContent = compareText;
      } else {
        regular.classList.remove('price__hidden');
        sale.classList.add('price__hidden');
        const regPrice = regular.querySelector('.price');
        if (regPrice) regPrice.textContent = priceText;
      }
    }
  }

  /**
   * On variant change, replace the picker's options with the re-rendered
   * ones (correct prices/disabled state for the new variant) while keeping
   * the shopper's selection when the plan is still available.
   * @param {Event & { promise?: Promise<any>, target?: Element }} event
   */
  #onProductSelect = async (event) => {
    // Ignore product-card events from elsewhere on the page.
    const sourceCard = event.target?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    try {
      const resolved = await event.promise;
      const html = resolved?.detail?.html;
      if (!html || !html.querySelector) return;

      const newPicker = html.querySelector('[data-vo-selling-plan]');
      if (!newPicker) {
        // New variant/product has no selling plans — hide the picker.
        this.hidden = true;
        return;
      }
      this.hidden = false;

      const prevId = this.#planId;

      // Swap inner content (radios + prices) for the new variant.
      this.innerHTML = newPicker.innerHTML;

      // Re-apply the previous selection if that plan is still available.
      const match = prevId
        ? this.querySelector(`input[data-vo-sp-radio][value="${CSS.escape(prevId)}"]`)
        : null;
      if (match && !match.disabled) {
        match.checked = true;
      } else {
        const oneTime = this.querySelector('input[data-vo-sp-radio][value=""]');
        if (oneTime && !oneTime.disabled) {
          oneTime.checked = true;
        } else {
          // Fall back to the first enabled plan.
          this.querySelector('input[data-vo-sp-radio]:not([disabled])')?.setAttribute('checked', 'true');
        }
      }

      this.#syncCta();
      // Horizon's product-form morphs the add-to-cart button on this same
      // event, resetting the label to the server-rendered "Add to cart".
      // Re-sync on the next frame so our "Subscribe" label wins.
      // The same frame also re-applies the plan price AFTER product-price.js
      // has replaced [ref="priceContainer"] (microtasks resolve before rAF).
      requestAnimationFrame(() => {
        this.#syncCta();
        this.#syncPrice();
      });
    } catch {
      // Aborted/failed fetches are expected on rapid variant changes — ignore.
    }
  };
}

if (!customElements.get('selling-plan-picker')) {
  customElements.define('selling-plan-picker', SellingPlanPicker);
}